// src/pages/LoginPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Input, Button, Card, message, Layout, Typography } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
// CSSファイルをコンポーネント内で明示的にインポート
import '../styles/login.css';

const { Content } = Layout;
const { Title } = Typography;

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // HashRouterでのリダイレクト元パス取得の修正
  const fromPath = location.state?.from?.pathname || '/';
  console.log('LoginPage - redirect target:', fromPath);

  // CSSが正しく適用されるように、コンポーネントのマウント時にスタイルを確認
  useEffect(() => {
    console.log('LoginPage mounted');
    
    // 開発環境でのCSSデバッグ情報
    if (process.env.NODE_ENV === 'development') {
      console.log('Current CSS classes applied:');
      const loginLayout = document.querySelector('.login-layout');
      if (loginLayout) {
        console.log('login-layout element found');
        console.log('Computed styles:', window.getComputedStyle(loginLayout));
      } else {
        console.warn('login-layout element not found');
      }
    }
  }, []);

  // ログイン処理
  const handleSubmit = async (values: { email: string; password: string }) => {
    try {
      setLoading(true);
      console.log('LoginPage - attempting login');
      await login(values.email, values.password);
      
      // ログイン成功時のメッセージ
      message.success('ログインに成功しました');
      console.log('LoginPage - login successful, redirecting to:', fromPath);
      
      // HashRouterでのリダイレクト：ハッシュ付きパスに正しく遷移
      if (fromPath === '/') {
        navigate('/', { replace: true });
      } else {
        // リダイレクト元がある場合は、そのパスに遷移
        navigate(fromPath, { replace: true });
      }
    } catch (error: any) {
      console.error('LoginPage - login error:', error);
      // エラーメッセージを表示
      let errorMessage = 'ログインに失敗しました';
      
      // Firebaseのエラーコードに基づいたメッセージ
      if (error.code === 'auth/insufficient-role') {
        errorMessage = error.message || '管理者権限がありません。管理Webは admin ロールのユーザーのみ利用できます。';
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = 'メールアドレスまたはパスワードが正しくありません';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'ログイン試行回数が多すぎます。しばらく時間をおいて再試行してください';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'ネットワークエラーが発生しました。インターネット接続を確認してください';
      }
      
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout className="login-layout">
      <Content className="login-content">
        <Card className="login-card" bordered={false}>
          <div className="login-logo">
            {/* ロゴがある場合は以下のようにインライン要素として実装 */}
            <div 
              style={{ 
                height: '80px', 
                backgroundColor: '#1565c0', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                borderRadius: '8px', 
                marginBottom: '16px',
                color: 'white',
                fontSize: '28px',
                fontWeight: 'bold',
                letterSpacing: '1px'
              }}
            >
              Care Tablet Web
            </div>
          </div>
          
          <Title level={2} className="login-title">
            Care Tablet Web 管理画面
          </Title>
          
          {/* デバッグ情報表示（開発時のみ） */}
          {process.env.NODE_ENV === 'development' && (
            <div 
              style={{ 
                marginBottom: '16px', 
                padding: '8px', 
                backgroundColor: '#f0f0f0', 
                borderRadius: '4px', 
                fontSize: '12px',
                border: '1px solid #ddd'
              }}
            >
              <div><strong>デバッグ情報:</strong></div>
              <div>リダイレクト先: {fromPath}</div>
              <div>現在のHash: {location.hash || '(empty)'}</div>
              <div>CSS確認: login-layout クラスが適用されているかコンソールで確認してください</div>
            </div>
          )}
          
          <Form
            name="login-form"
            initialValues={{ remember: true }}
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
            style={{ width: '100%' }}
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'メールアドレスを入力してください' },
                { type: 'email', message: '有効なメールアドレスを入力してください' }
              ]}
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder="メールアドレス" 
                autoComplete="username"
                style={{ height: '40px' }}
              />
            </Form.Item>
            
            <Form.Item
              name="password"
              rules={[{ required: true, message: 'パスワードを入力してください' }]}
            >
              <Input.Password 
                prefix={<LockOutlined />} 
                placeholder="パスワード" 
                autoComplete="current-password"
                style={{ height: '40px' }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                className="login-button" 
                block 
                loading={loading}
                style={{
                  height: '40px',
                  fontSize: '16px',
                  backgroundColor: '#1565c0',
                  borderColor: '#1565c0'
                }}
              >
                ログイン
              </Button>
            </Form.Item>
          </Form>
          
          <div className="login-footer">
            <p>© 2025 DI-M LLC. All rights reserved.</p>
          </div>
        </Card>
      </Content>
    </Layout>
  );
};

export default LoginPage;